import { create } from 'zustand';
import { vehicleService } from '../services/vehicleService';
import { Vehicle, CreateVehicleRequest, UpdateVehicleRequest } from '../types';
import { handleApiError, formatErrorForDisplay, logError } from '../lib/errorHandler';

interface VehicleState {
  // State
  vehicles: Vehicle[];
  primaryVehicle: Vehicle | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchMyVehicles: () => Promise<void>;
  addVehicle: (data: CreateVehicleRequest) => Promise<Vehicle>;
  updateVehicle: (vehicleId: string, data: UpdateVehicleRequest) => Promise<Vehicle>;
  deleteVehicle: (vehicleId: string) => Promise<void>;
  setPrimaryVehicle: (vehicleId: string) => Promise<void>;
  clearError: () => void;
}

/**
 * Vehicle Store
 * Manages user vehicles with CRUD operations
 */
export const useVehicleStore = create<VehicleState>((set, get) => ({
  // Initial state
  vehicles: [],
  primaryVehicle: null,
  isLoading: false,
  error: null,

  // Fetch my vehicles
  fetchMyVehicles: async () => {
    set({ isLoading: true, error: null });

    try {
      const vehicles = await vehicleService.getMyVehicles();

      // Find primary vehicle
      const primaryVehicle = vehicles.find((v) => v.isPrimary) || null;

      set({
        vehicles,
        primaryVehicle,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      const appError = handleApiError(error);
      logError(appError, 'VehicleStore.fetchMyVehicles');
      set({
        isLoading: false,
        error: formatErrorForDisplay(appError),
      });
    }
  },

  // Add vehicle
  addVehicle: async (data: CreateVehicleRequest) => {
    set({ isLoading: true, error: null });

    try {
      const newVehicle = await vehicleService.createVehicle(data);

      set((state) => {
        const vehicles = [...state.vehicles, newVehicle];
        const primaryVehicle = newVehicle.isPrimary ? newVehicle : state.primaryVehicle;

        return {
          vehicles,
          primaryVehicle,
          isLoading: false,
          error: null,
        };
      });

      return newVehicle;
    } catch (error) {
      const appError = handleApiError(error);
      logError(appError, 'VehicleStore.addVehicle');
      set({
        isLoading: false,
        error: formatErrorForDisplay(appError),
      });
      throw error;
    }
  },

  // Update vehicle
  updateVehicle: async (vehicleId: string, data: UpdateVehicleRequest) => {
    set({ isLoading: true, error: null });

    try {
      const updatedVehicle = await vehicleService.updateVehicle(vehicleId, data);

      set((state) => {
        const vehicles = state.vehicles.map((v) =>
          v.id === vehicleId ? updatedVehicle : v
        );
        const primaryVehicle = updatedVehicle.isPrimary ? updatedVehicle : state.primaryVehicle;

        return {
          vehicles,
          primaryVehicle,
          isLoading: false,
          error: null,
        };
      });

      return updatedVehicle;
    } catch (error) {
      const appError = handleApiError(error);
      logError(appError, 'VehicleStore.updateVehicle');
      set({
        isLoading: false,
        error: formatErrorForDisplay(appError),
      });
      throw error;
    }
  },

  // Delete vehicle
  deleteVehicle: async (vehicleId: string) => {
    set({ isLoading: true, error: null });

    try {
      await vehicleService.deleteVehicle(vehicleId);

      set((state) => {
        const vehicles = state.vehicles.filter((v) => v.id !== vehicleId);
        const primaryVehicle =
          state.primaryVehicle?.id === vehicleId ? null : state.primaryVehicle;

        return {
          vehicles,
          primaryVehicle,
          isLoading: false,
          error: null,
        };
      });
    } catch (error) {
      const appError = handleApiError(error);
      logError(appError, 'VehicleStore.deleteVehicle');
      set({
        isLoading: false,
        error: formatErrorForDisplay(appError),
      });
      throw error;
    }
  },

  // Set primary vehicle
  setPrimaryVehicle: async (vehicleId: string) => {
    set({ isLoading: true, error: null });

    try {
      const updatedVehicle = await vehicleService.setPrimaryVehicle(vehicleId);

      set((state) => {
        // Mark all vehicles as not primary, except the new primary
        const vehicles = state.vehicles.map((v) => ({
          ...v,
          isPrimary: v.id === vehicleId,
        }));

        return {
          vehicles,
          primaryVehicle: updatedVehicle,
          isLoading: false,
          error: null,
        };
      });
    } catch (error) {
      const appError = handleApiError(error);
      logError(appError, 'VehicleStore.setPrimaryVehicle');
      set({
        isLoading: false,
        error: formatErrorForDisplay(appError),
      });
      throw error;
    }
  },

  // Clear error
  clearError: () => {
    set({ error: null });
  },
}));
